/**
 * Complaint Form — storefront widget
 *
 * Flow: fetch a scoped JWT from the App Proxy token endpoint -> upload photos
 * individually to the `uploadComplaintPhoto` Firebase function (each returns a
 * signed `{path, sig}` reference) -> on submit, build a nested JSON payload
 * from the plain HTML form fields and post it through the App Proxy to
 * `submitComplaint`. Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var form = document.getElementById('ComplaintForm');
  if (!form) return;

  var TOKEN_ENDPOINT = '/apps/moliani-try-in-room/complaint-token';
  var SUBMIT_ENDPOINT = '/apps/moliani-try-in-room/complaint';
  var UPLOAD_ENDPOINT = 'https://europe-west1-gokiga.cloudfunctions.net/uploadComplaintPhoto';
  var MAX_CONCURRENT_UPLOADS = 2;
  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  var i18n = {
    validationRequired: form.dataset.i18nValidationRequired || '',
    validationEmail: form.dataset.i18nValidationEmail || '',
    validationMinOneRequestType: form.dataset.i18nValidationMinOneRequestType || '',
    validationRequestOtherRequired: form.dataset.i18nValidationRequestOtherRequired || '',
    validationConsentRequired: form.dataset.i18nValidationConsentRequired || '',
    sending: form.dataset.i18nSending || '',
    submitLabel: form.dataset.i18nSubmitLabel || '',
    errorMessage: form.dataset.i18nErrorMessage || '',
    errorRateLimited: form.dataset.i18nErrorRateLimited || ''
  };

  var dropzone = document.getElementById('ComplaintPhotosDropzone');
  var fileInput = document.getElementById('ComplaintPhotosInput');
  var previewsContainer = document.getElementById('ComplaintPhotoPreviews');
  var previewTemplate = document.getElementById('ComplaintPhotoPreviewTemplate');
  var photosErrorEl = document.getElementById('ComplaintPhotosError');
  var requestTypesErrorEl = document.getElementById('ComplaintRequestTypesError');
  var requestOtherCheckbox = document.getElementById('ComplaintRequestOther');
  var requestOtherWrapper = document.getElementById('ComplaintRequestOtherWrapper');
  var requestOtherTextarea = document.getElementById('ComplaintRequestOtherText');
  var requestOtherErrorEl = document.getElementById('ComplaintRequestOtherError');
  var consentInput = document.getElementById('ComplaintConsent');
  var consentErrorEl = document.getElementById('ComplaintConsentError');
  var submitButton = document.getElementById('ComplaintSubmitButton');
  var submitButtonText = submitButton ? submitButton.querySelector('.btn__text') : null;
  var successEl = document.getElementById('ComplaintFormSuccess');
  var errorEl = document.getElementById('ComplaintFormError');

  var maxFiles = parseInt(dropzone.dataset.maxFiles, 10) || 5;
  var maxSizeBytes = (parseFloat(dropzone.dataset.maxSizeMb) || 15) * 1024 * 1024;

  var photos = []; // { id, file, status: pending|uploading|done|error, cardEl, progressEl, dataUrl, path, sig, width, height }
  var photoSeq = 0;

  var authToken = null;
  var authTokenExpiry = 0;

  // ---------------------------------------------------------------------
  // Auth token (App Proxy issues a JWT scoped to aud: "complaint-form")
  // ---------------------------------------------------------------------
  function fetchToken() {
    if (authToken && Date.now() < authTokenExpiry - 60000) {
      return Promise.resolve(authToken);
    }
    return fetch(TOKEN_ENDPOINT, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('token_fetch_failed_' + response.status);
        return response.json();
      })
      .then(function (result) {
        if (!result || !result.success || !result.token) throw new Error('token_invalid_response');
        authToken = result.token;
        authTokenExpiry = Date.now() + (result.expiresIn || 3600) * 1000;
        return authToken;
      });
  }

  // Warm the token as soon as the form is available so the first photo
  // upload / submit doesn't have to wait on it. Non-fatal if it fails now —
  // it is retried lazily (and any real failure becomes visible) on first use.
  fetchToken().catch(function (error) {
    console.error('Complaint form: token pre-fetch failed', error);
  });

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------
  function fileToDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = function () {
        reject(reader.error || new Error('file_read_failed'));
      };
      reader.readAsDataURL(file);
    });
  }

  function fieldValue(name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }

  function resolveLocale() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('de') === 0 ? 'de' : 'pl';
  }

  // ---------------------------------------------------------------------
  // Field-level validation (native constraint checks, custom rendering)
  // ---------------------------------------------------------------------
  function setFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    var existing = field.parentNode.querySelector('.complaint-form__field-error');
    if (!existing) {
      existing = document.createElement('p');
      existing.className = 'form-message form-message--small form-message--error complaint-form__field-error';
      existing.setAttribute('role', 'alert');
      field.insertAdjacentElement('afterend', existing);
    }
    existing.textContent = message;
  }

  function clearFieldError(field) {
    field.removeAttribute('aria-invalid');
    var existing = field.parentNode.querySelector('.complaint-form__field-error');
    if (existing) existing.parentNode.removeChild(existing);
  }

  function clearAllErrors() {
    Array.prototype.forEach.call(form.querySelectorAll('.complaint-form__field-error'), function (el) {
      el.parentNode.removeChild(el);
    });
    Array.prototype.forEach.call(form.querySelectorAll('[aria-invalid="true"]'), function (el) {
      el.removeAttribute('aria-invalid');
    });
    requestTypesErrorEl.style.display = 'none';
    requestTypesErrorEl.textContent = '';
    requestOtherErrorEl.style.display = 'none';
    requestOtherErrorEl.textContent = '';
    consentErrorEl.style.display = 'none';
    consentErrorEl.textContent = '';
    errorEl.style.display = 'none';
    errorEl.textContent = '';
  }

  function validateForm() {
    clearAllErrors();
    var firstInvalid = null;

    Array.prototype.forEach.call(form.querySelectorAll('input[required], textarea[required]'), function (field) {
      if (!field.checkValidity()) {
        var message = field.validity.typeMismatch ? i18n.validationEmail : i18n.validationRequired;
        setFieldError(field, message);
        if (!firstInvalid) firstInvalid = field;
      }
    });

    var anyRequestTypeChecked = form.querySelectorAll('input[name="requestTypes"]:checked').length > 0;
    if (!anyRequestTypeChecked) {
      requestTypesErrorEl.textContent = i18n.validationMinOneRequestType;
      requestTypesErrorEl.style.display = 'flex';
      if (!firstInvalid) firstInvalid = form.querySelector('input[name="requestTypes"]');
    }

    // "Inne" is only meaningful together with the customer's own description.
    if (requestOtherCheckbox.checked && !requestOtherTextarea.value.trim()) {
      requestOtherErrorEl.textContent = i18n.validationRequestOtherRequired;
      requestOtherErrorEl.style.display = 'flex';
      if (!firstInvalid) firstInvalid = requestOtherTextarea;
    }

    if (!consentInput.checked) {
      consentErrorEl.textContent = i18n.validationConsentRequired;
      consentErrorEl.style.display = 'flex';
      if (!firstInvalid) firstInvalid = consentInput;
    }

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  }

  form.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('input[required], textarea[required]')) {
      clearFieldError(e.target);
    }
    if (e.target === requestOtherTextarea) {
      requestOtherErrorEl.style.display = 'none';
    }
  });

  // Show/hide the free-text box for the "Inne" request type. Hiding also clears the value
  // so an abandoned draft is never submitted with an unchecked "Inne".
  function toggleRequestOther(focusTextarea) {
    if (requestOtherCheckbox.checked) {
      requestOtherWrapper.style.display = '';
      if (focusTextarea) requestOtherTextarea.focus();
    } else {
      requestOtherWrapper.style.display = 'none';
      requestOtherTextarea.value = '';
      requestOtherErrorEl.style.display = 'none';
      requestOtherErrorEl.textContent = '';
    }
  }

  // Sync on load without stealing focus (browsers restore checkbox state on soft reload).
  toggleRequestOther(false);
  form.addEventListener('change', function (e) {
    if (e.target.name === 'requestTypes') {
      requestTypesErrorEl.style.display = 'none';
    }
    if (e.target === requestOtherCheckbox) {
      toggleRequestOther(true);
    }
    if (e.target === consentInput) {
      consentErrorEl.style.display = 'none';
    }
  });

  // ---------------------------------------------------------------------
  // Photo upload
  // ---------------------------------------------------------------------
  function updateSubmitState() {
    var uploading = photos.some(function (p) {
      return p.status === 'uploading';
    });
    submitButton.disabled = uploading;
  }

  function showPhotosError(message) {
    photosErrorEl.textContent = message;
    photosErrorEl.style.display = 'flex';
  }

  function clearPhotosError() {
    photosErrorEl.style.display = 'none';
    photosErrorEl.textContent = '';
  }

  function setCardState(item, state) {
    item.cardEl.classList.remove('is-pending', 'is-uploading', 'is-done', 'is-error');
    item.cardEl.classList.add('is-' + state);
  }

  function createPreviewCard(item) {
    var node = previewTemplate.content.firstElementChild.cloneNode(true);
    var img = node.querySelector('.photo-preview-card__img');
    img.src = item.objectUrl;
    img.alt = item.file.name;

    node.querySelector('.photo-preview-card__remove').addEventListener('click', function () {
      removePhoto(item.id);
    });
    node.querySelector('.photo-preview-card__retry').addEventListener('click', function () {
      uploadPhoto(item);
    });

    item.cardEl = node;
    item.progressEl = node.querySelector('.photo-preview-card__progress span');
    return node;
  }

  function removePhoto(id) {
    var index = photos.findIndex(function (p) {
      return p.id === id;
    });
    if (index === -1) return;
    var item = photos[index];
    photos.splice(index, 1);
    if (item.cardEl && item.cardEl.parentNode) item.cardEl.parentNode.removeChild(item.cardEl);
    if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
    updateSubmitState();
  }

  function uploadPhoto(item) {
    item.status = 'uploading';
    setCardState(item, 'uploading');
    if (item.progressEl) item.progressEl.style.width = '15%';
    updateSubmitState();

    return fetchToken()
      .then(function (token) {
        if (item.progressEl) item.progressEl.style.width = '55%';
        return fetch(UPLOAD_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
          },
          body: JSON.stringify({ imageData: item.dataUrl })
        });
      })
      .then(function (response) {
        return response.json().then(function (result) {
          return { ok: response.ok, status: response.status, result: result };
        });
      })
      .then(function (outcome) {
        // The card may have been removed by the user while this was in flight.
        if (photos.indexOf(item) === -1) return;

        if (outcome.ok && outcome.result && outcome.result.success) {
          item.status = 'done';
          item.path = outcome.result.path;
          item.sig = outcome.result.sig;
          item.width = outcome.result.width;
          item.height = outcome.result.height;
          if (item.progressEl) item.progressEl.style.width = '100%';
          setCardState(item, 'done');
        } else {
          item.status = 'error';
          setCardState(item, 'error');
          console.error('Complaint form: photo upload rejected', outcome.status, outcome.result);
        }
        updateSubmitState();
      })
      .catch(function (error) {
        if (photos.indexOf(item) === -1) return;
        item.status = 'error';
        setCardState(item, 'error');
        updateSubmitState();
        console.error('Complaint form: photo upload failed', error);
      });
  }

  function queueUploads() {
    var uploading = photos.filter(function (p) {
      return p.status === 'uploading';
    }).length;
    var pending = photos.filter(function (p) {
      return p.status === 'pending' && p.dataUrl;
    });
    var slots = MAX_CONCURRENT_UPLOADS - uploading;
    for (var i = 0; i < slots && i < pending.length; i++) {
      uploadPhoto(pending[i]).then(queueUploads);
    }
  }

  function handleFiles(fileList) {
    clearPhotosError();
    var incoming = Array.prototype.slice.call(fileList);
    var accepted = [];

    incoming.forEach(function (file) {
      if (photos.length + accepted.length >= maxFiles) {
        showPhotosError(dropzone.dataset.errTooMany);
        return;
      }
      if (ALLOWED_TYPES.indexOf(file.type) === -1) {
        showPhotosError(dropzone.dataset.errBadType);
        return;
      }
      if (file.size > maxSizeBytes) {
        showPhotosError(dropzone.dataset.errTooLarge);
        return;
      }
      accepted.push(file);
    });

    accepted.forEach(function (file) {
      var item = {
        id: 'photo-' + photoSeq++,
        file: file,
        status: 'pending',
        objectUrl: URL.createObjectURL(file),
        dataUrl: null
      };
      photos.push(item);
      var card = createPreviewCard(item);
      previewsContainer.appendChild(card);

      fileToDataUrl(file)
        .then(function (dataUrl) {
          item.dataUrl = dataUrl;
          queueUploads();
        })
        .catch(function (error) {
          item.status = 'error';
          setCardState(item, 'error');
          updateSubmitState();
          console.error('Complaint form: could not read file', error);
        });
    });

    updateSubmitState();
  }

  fileInput.addEventListener('change', function (e) {
    handleFiles(e.target.files);
    fileInput.value = '';
  });

  ['dragover', 'dragenter'].forEach(function (evt) {
    dropzone.addEventListener(evt, function (e) {
      e.preventDefault();
      dropzone.classList.add('is-dragover');
    });
  });
  ['dragleave', 'dragend'].forEach(function (evt) {
    dropzone.addEventListener(evt, function (e) {
      e.preventDefault();
      dropzone.classList.remove('is-dragover');
    });
  });
  dropzone.addEventListener('drop', function (e) {
    e.preventDefault();
    dropzone.classList.remove('is-dragover');
    if (e.dataTransfer && e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  });

  // ---------------------------------------------------------------------
  // Payload + submit
  // ---------------------------------------------------------------------
  function buildProducts() {
    var rows = [];
    for (var i = 0; i < 3; i++) {
      var name = fieldValue('products[' + i + '].name');
      var qty = fieldValue('products[' + i + '].qty');
      var grossPrice = fieldValue('products[' + i + '].grossPrice');
      if (!name && !qty && !grossPrice) continue;
      var row = { name: name };
      if (qty !== '') row.qty = Number(qty);
      if (grossPrice !== '') row.grossPrice = Number(grossPrice);
      rows.push(row);
    }
    return rows;
  }

  function buildPayload() {
    return {
      order: {
        orderNumber: fieldValue('order.orderNumber'),
        orderDate: fieldValue('order.orderDate'),
        invoiceNumber: fieldValue('order.invoiceNumber'),
        defectDiscoveryDate: fieldValue('order.defectDiscoveryDate')
      },
      customer: {
        name: fieldValue('customer.name'),
        address: fieldValue('customer.address'),
        phone: fieldValue('customer.phone'),
        email: fieldValue('customer.email')
      },
      products: buildProducts(),
      defectDescription: fieldValue('defectDescription'),
      requestTypes: Array.prototype.slice
        .call(form.querySelectorAll('input[name="requestTypes"]:checked'))
        .map(function (el) {
          return el.value;
        }),
      requestOtherText: requestOtherCheckbox.checked ? requestOtherTextarea.value.trim() : '',
      customerNotes: fieldValue('customerNotes'),
      website: fieldValue('website'),
      consent: !!consentInput.checked,
      photos: photos
        .filter(function (p) {
          return p.status === 'done';
        })
        .map(function (p) {
          return { path: p.path, sig: p.sig, width: p.width, height: p.height };
        }),
      locale: resolveLocale()
    };
  }

  function setSubmitting(isSubmitting) {
    submitButton.disabled = isSubmitting;
    if (isSubmitting) {
      submitButton.classList.add('btn--loading');
      if (submitButtonText) submitButtonText.textContent = i18n.sending;
    } else {
      submitButton.classList.remove('btn--loading');
      if (submitButtonText) submitButtonText.textContent = i18n.submitLabel;
    }
  }

  function showFormError(message) {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
    errorEl.focus();
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();

    if (photos.some(function (p) { return p.status === 'uploading'; })) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    fetchToken()
      .then(function () {
        return fetch(SUBMIT_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload())
        });
      })
      .then(function (response) {
        return response.json().then(function (result) {
          return { ok: response.ok, status: response.status, result: result };
        });
      })
      .then(function (outcome) {
        if (outcome.ok && outcome.result && outcome.result.success) {
          form.style.display = 'none';
          successEl.style.display = 'block';
          var refEl = successEl.querySelector('[data-ref-number]');
          if (refEl) refEl.textContent = outcome.result.referenceNumber || '';
          successEl.focus();
        } else if (outcome.status === 429) {
          setSubmitting(false);
          showFormError(i18n.errorRateLimited);
        } else {
          setSubmitting(false);
          showFormError(i18n.errorMessage);
          console.error('Complaint form: submit rejected', outcome.status, outcome.result);
        }
      })
      .catch(function (error) {
        setSubmitting(false);
        showFormError(i18n.errorMessage);
        console.error('Complaint form: submit failed', error);
      });
  });
})();
